import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Menu, X, Building2, Users, BarChart3, BookOpen, LogOut, Shield, FileText, Receipt, Kanban, ClipboardList, Target, CreditCard, ScrollText, DollarSign, MessageSquare, MessageCircle, AlertTriangle, CalendarDays, Wrench } from 'lucide-react';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const navItems = [
  { path: '/admin/revenue', label: 'דאשבורד הכנסות', icon: DollarSign, exact: false },
  { path: '/admin/billing-orgs', label: 'ניהול חבילות', icon: CreditCard, exact: false },
  { path: '/admin/orgs', label: 'ניהול ארגונים', icon: Building2, exact: false },
  { path: '/admin', label: 'מסע לקוח', icon: Kanban, exact: true },
  { path: '/admin/leads', label: 'לידים', icon: FileText, exact: false },
  { path: '/admin/referrers', label: 'תוכנית מפנים', icon: Users, exact: false },
  // Calendar + tasks are conceptually one workflow (tasks with dueDate
  // show up on the calendar) — keep them adjacent so they're easy to
  // hop between.
  { path: '/admin/calendar', label: '📅 יומן אדמין', icon: CalendarDays, exact: false },
  { path: '/admin/dev', label: '📋 ניהול משימות', icon: ClipboardList, exact: false },
  { path: '/admin/users', label: 'משתמשים', icon: Users, exact: false },
  { path: '/admin/sms-senders', label: 'שולחי SMS', icon: MessageSquare, exact: false },
  { path: '/admin/stuck-sms', label: 'SMS תקועים', icon: AlertTriangle, exact: false },
  { path: '/admin/analytics', label: 'אנליטיקה', icon: BarChart3, exact: false },
  { path: '/admin/logs', label: 'לוגים ומערכת', icon: ScrollText, exact: false },
  { path: '/admin/articles', label: 'מרכז ידע', icon: BookOpen, exact: false },
  { path: '/admin/strategic-plan', label: 'תכנית אסטרטגית', icon: Target, exact: false },
  { path: '/admin/quotes', label: 'הצעות מחיר', icon: Receipt, exact: false },
  { path: '/admin/payment-forms', label: 'טפסי תשלום', icon: CreditCard, exact: false },
  { path: '/admin/system-terminal', label: 'מסוף סליקה', icon: CreditCard, exact: false },
  { path: '/admin/maytapi', label: 'WhatsApp Maytapi', icon: MessageCircle, exact: false },
  { path: '/admin/debug-tranzila', label: '🔧 Tranzila Debug', icon: Wrench, exact: false },
];

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full min-h-0">
      {/* Logo */}
      <div className="px-5 py-6 border-b border-gray-700 bg-slate-950">
        <div className="flex items-center gap-2">
          <Shield size={22} className="text-blue-400" />
          <div>
            <div className="text-lg font-bold text-blue-400">Amutech</div>
            <div className="text-xs text-gray-400">Super Admin Panel</div>
          </div>
        </div>
      </div>

      {/* Nav — scrolls when the item list is taller than the viewport */}
      <nav className="flex-1 min-h-0 overflow-y-auto py-4">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.exact}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-5 py-3 text-sm font-medium transition-all border-r-4 ${
                isActive
                  ? 'bg-blue-900/40 text-blue-400 border-blue-400'
                  : 'text-gray-300 hover:bg-gray-800 hover:text-white border-transparent'
              }`
            }
          >
            <item.icon size={17} />
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* User + Logout */}
      <div className="px-5 py-4 border-t border-gray-700 bg-slate-950">
        <div className="text-xs text-gray-400 truncate mb-1">{user?.email}</div>
        <div className="text-xs text-gray-600 mb-3">Super Admin</div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 w-full px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors"
        >
          <LogOut size={15} />
          התנתק
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-100 font-sans" dir="rtl">

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-60 bg-gray-900 text-white flex-shrink-0 sticky top-0 h-screen">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 right-0 left-0 z-50 flex items-center justify-between px-4 py-3 bg-gray-900 text-white shadow-md">
        <div className="flex items-center gap-2">
          <Shield size={18} className="text-blue-400" />
          <span className="font-bold text-blue-400 text-sm">Admin</span>
        </div>
        <button onClick={() => setMobileOpen(!mobileOpen)} className="p-1">
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40" dir="rtl">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="absolute top-0 right-0 w-64 h-full bg-gray-900 text-white shadow-xl flex flex-col">
            {/* Spacer so the panel clears the fixed mobile header */}
            <div className="h-14 shrink-0" />
            <div className="flex-1 min-h-0">
              <SidebarContent />
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto md:p-8 p-4 pt-16 md:pt-8">
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
